import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export interface BreadcrumbEntry {
  label: string;
  /** Absent on the last (current-page) entry. */
  to?: string;
}

interface RouteConfig {
  label: string;
  /** Path of a conceptual parent to nest under. */
  parent?: string;
}

const ROUTES: Record<string, RouteConfig> = {
  '/app': { label: 'Home' },
  '/app/report': { label: 'Report' },
  '/app/questionnaires': { label: 'Questionnaires' },
  '/app/account': { label: 'Account' },
  '/app/account/edit': { label: 'Edit profile', parent: '/app/account' },
  '/app/bookings': { label: 'Bookings' },
  '/app/whats-new': { label: 'What’s new' },
};

function routeConfigFor(pathname: string): RouteConfig | null {
  const exact = ROUTES[pathname];
  if (exact) return exact;
  // `/app/questionnaires/:id` — no stored per-item title (CP-P2-T02's
  // questionnaire titles are type-derived, not named), so the current-page
  // crumb is an honest generic label rather than a fabricated one.
  if (pathname.startsWith('/app/questionnaires/')) {
    return { label: 'Questionnaire', parent: '/app/questionnaires' };
  }
  return null;
}

/** Route → breadcrumb trail. Every entry but the last carries a `to` link;
 * the last is the current page. Only ever rendered inside the authenticated
 * `/app` shell (`Topbar`), so "Home" always means the dashboard, not the
 * public marketing site's `/`. */
export function useBreadcrumbs(): BreadcrumbEntry[] {
  const { pathname } = useLocation();

  return useMemo(() => {
    if (pathname === '/app') return [{ label: 'Home' }];

    const config = routeConfigFor(pathname);
    if (!config) return [{ label: 'Home', to: '/app' }];

    const trail: BreadcrumbEntry[] = [{ label: 'Home', to: '/app' }];
    if (config.parent) {
      const parentConfig = ROUTES[config.parent];
      if (parentConfig) trail.push({ label: parentConfig.label, to: config.parent });
    }
    trail.push({ label: config.label });
    return trail;
  }, [pathname]);
}
